import { memo, type FC } from "react";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import {
  ORDER_ACTIONS,
  ORDER_COMPARISONS,
  PERIOD_UNITS,
  type OrderAction,
  type OrderActivityCondition,
  type OrderComparison,
  type PeriodUnit,
} from "../../CreateSegment.Constants";
import { parsePositiveInteger } from "../../CreateSegment.Utils";
import { useSegmentLabel, useSegmentOptions } from "../../UseSegmentLabel";
import { ConditionFieldsRow, SegmentSelect, type ConditionFieldsProps } from "../ConditionControls";

const OrderActivityFields: FC<ConditionFieldsProps<OrderActivityCondition>> = ({
  condition,
  onChange,
}) => {
  const label = useSegmentLabel();
  const actionOptions = useSegmentOptions("fields.orderActivity.actions", ORDER_ACTIONS);
  const comparisonOptions = useSegmentOptions("fields.orderActivity.comparisons", ORDER_COMPARISONS);
  const unitOptions = useSegmentOptions("fields.orderActivity.units", PERIOD_UNITS);
  const isPlacedOrder = condition.action === "placedOrder";

  return (
    <ConditionFieldsRow label={label("fields.orderActivity.label")}>
      <SegmentSelect
        className="segment-builder__control--lg"
        aria-label={label("fields.orderActivity.actionAria")}
        value={condition.action}
        options={actionOptions}
        onChange={(action: OrderAction) => onChange({ ...condition, action })}
      />
      {isPlacedOrder && (
        <>
          <SegmentSelect
            className="segment-builder__control--sm"
            aria-label={label("fields.orderActivity.comparisonAria")}
            value={condition.comparison}
            options={comparisonOptions}
            onChange={(comparison: OrderComparison) => onChange({ ...condition, comparison })}
          />
          <StyledTextInput
            className="campaign-flow__input segment-builder__number segment-builder__number--count"
            aria-label={label("fields.orderActivity.countAria")}
            inputMode="numeric"
            value={condition.count ?? ""}
            onChange={event => onChange({ ...condition, count: parsePositiveInteger(event.target.value) })}
          />
          <span className="segment-builder__fields-text">{label("fields.orderActivity.times")}</span>
        </>
      )}
      <span className="segment-builder__fields-text">
        {label("fields.orderActivity.windows.inTheLast")}
      </span>
      <StyledTextInput
        className="campaign-flow__input segment-builder__number"
        aria-label={label("fields.orderActivity.periodAria")}
        inputMode="numeric"
        value={condition.periodValue ?? ""}
        onChange={event =>
          onChange({ ...condition, periodValue: parsePositiveInteger(event.target.value) })
        }
      />
      <SegmentSelect
        className="segment-builder__control--unit"
        aria-label={label("fields.orderActivity.unitAria")}
        value={condition.periodUnit}
        options={unitOptions}
        onChange={(periodUnit: PeriodUnit) => onChange({ ...condition, periodUnit })}
      />
    </ConditionFieldsRow>
  );
};

export default memo(OrderActivityFields);
